/**
 * The orphan review, client side — ROADMAP 7.8.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS HOOK NEVER DECIDES ANYTHING
 *
 * It fetches the proposal, holds what the user has typed, and posts their
 * decisions. The one rule it enforces locally is the one the server enforces
 * again: a leg whose price was EDITED is sent as `EDIT`, and stays ADJUSTED
 * for the rest of its life. A screen that quietly "corrected" a proposal and
 * sent it as `CONFIRM` would launder a typed price into a broker-sourced one.
 *
 * NOT POLLED. A review is a thing a person sits down to do; re-fetching it
 * underneath them would replace the prices they are halfway through editing.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useState } from 'react'
import { api, qs } from '@/lib/api'

export interface ReviewLeg {
  tradeLegId: string
  label: string
  /** The contract's expiry, as ISO. Empty when the server did not send one. */
  expiry: string
  ourQty: number
  orphanedQty: number
  proposedPrice: number | null
  allocated: boolean
  proposedExitAt: string | null
  unmatched: number
}

export interface ReviewTrade {
  tradeId: string
  underlying: string
  openedAt: string
  orphanedAt: string | null
  legs: ReviewLeg[]
}

export interface ReviewPayload {
  connectionId: string
  trades: ReviewTrade[]
  unclaimed: number
  unreadable: string | null
}

export type ReviewState =
  | { readonly kind: 'IDLE' }
  | { readonly kind: 'LOADING' }
  /** The request failed. NOT "nothing to review" — those must not look alike. */
  | { readonly kind: 'ERROR'; readonly why: string }
  | { readonly kind: 'READY'; readonly review: ReviewPayload }

/**
 * Validate at the boundary.
 *
 * `api.get<T>` is a type ASSERTION, not a check. A shape mismatch reaching
 * `.map` would take the Portfolio screen down with it, and this panel is a
 * guest on that screen.
 */
function asReview(value: unknown): ReviewPayload | null {
  if (typeof value !== 'object' || value === null) return null
  const v = value as Partial<ReviewPayload>
  if (!Array.isArray(v.trades)) return null
  if (typeof v.connectionId !== 'string') return null

  const legOf = (l: unknown): ReviewLeg | null => {
    if (typeof l !== 'object' || l === null) return null
    const x = l as Partial<ReviewLeg>
    if (typeof x.tradeLegId !== 'string') return null
    if (typeof x.orphanedQty !== 'number') return null
    return {
      tradeLegId: x.tradeLegId,
      label: typeof x.label === 'string' ? x.label : x.tradeLegId,
      /*
       * An absent expiry becomes '', which `contractExpired` refuses to read
       * as expired — so an older server that does not send it makes the panel
       * fall back to the broker wording rather than claiming a contract died.
       */
      expiry: typeof x.expiry === 'string' ? x.expiry : '',
      ourQty: typeof x.ourQty === 'number' ? x.ourQty : 0,
      orphanedQty: x.orphanedQty,
      // A non-number proposal is NO proposal. Never coerced to zero: zero is
      // a price, and it is the wrong one.
      proposedPrice: typeof x.proposedPrice === 'number' && x.proposedPrice > 0 ? x.proposedPrice : null,
      allocated: x.allocated === true,
      proposedExitAt: typeof x.proposedExitAt === 'string' ? x.proposedExitAt : null,
      unmatched: typeof x.unmatched === 'number' ? x.unmatched : x.orphanedQty,
    }
  }

  return {
    connectionId: v.connectionId,
    unclaimed: typeof v.unclaimed === 'number' ? v.unclaimed : 0,
    unreadable: typeof v.unreadable === 'string' ? v.unreadable : null,
    trades: v.trades.flatMap((t) => {
      if (typeof t !== 'object' || t === null) return []
      const x = t as Partial<ReviewTrade>
      if (typeof x.tradeId !== 'string' || !Array.isArray(x.legs)) return []
      const legs = x.legs.map(legOf).filter((l): l is ReviewLeg => l !== null)
      if (legs.length === 0) return []
      return [{
        tradeId: x.tradeId,
        underlying: typeof x.underlying === 'string' ? x.underlying : '—',
        openedAt: typeof x.openedAt === 'string' ? x.openedAt : '',
        orphanedAt: typeof x.orphanedAt === 'string' ? x.orphanedAt : null,
        legs,
      }]
    }),
  }
}

export type LegDecision =
  | { tradeLegId: string; action: 'CONFIRM'; price: number; exitedAt?: string }
  | { tradeLegId: string; action: 'EDIT'; price: number; exitedAt?: string }
  | { tradeLegId: string; action: 'DEFER' }

export interface ConfirmResult {
  resolved: number
  deferred: number
  tradesClosed: number
}

export interface OrphanReview {
  state: ReviewState
  /** How many legs are awaiting a decision. 0 hides the whole panel. */
  pending: number
  refresh: () => void
  submit: (decisions: LegDecision[]) => Promise<ConfirmResult>
}

export function useOrphanReview(connectionId: string | null): OrphanReview {
  const [state, setState] = useState<ReviewState>({ kind: 'IDLE' })
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    if (!connectionId) {
      setState({ kind: 'IDLE' })
      return
    }
    let cancelled = false
    setState({ kind: 'LOADING' })
    void api
      .get<unknown>(`/portfolio/review${qs({ connectionId })}`)
      .then((r) => {
        if (cancelled) return
        const parsed = asReview(r)
        setState(
          parsed === null
            ? { kind: 'ERROR', why: 'the review came back in a shape this build does not understand' }
            : { kind: 'READY', review: parsed },
        )
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setState({ kind: 'ERROR', why: e instanceof Error ? e.message : 'could not read the review' })
      })
    return () => {
      cancelled = true
    }
  }, [connectionId, nonce])

  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  const submit = useCallback(
    async (decisions: LegDecision[]): Promise<ConfirmResult> => {
      const r = await api.post<ConfirmResult>('/portfolio/review', { decisions })
      // Re-fetch rather than patching local state: the server decides which
      // trades left review, and guessing that here is how the two drift.
      setNonce((n) => n + 1)
      return r
    },
    [],
  )

  const pending =
    state.kind === 'READY' ? state.review.trades.reduce((n, t) => n + t.legs.length, 0) : 0

  return { state, pending, refresh, submit }
}
